/**
 * Life Blueprint V2.9.9 — M02 ReferenceData · Controlled Batch 04
 *   DB_60甲子 (Sheet39, A1:G61 — 60 audited records)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STATUS
 *   • DB_60甲子 dataset status = AUTHORITATIVE_DATASET_COMPLETE (60 rows).
 *   • M02 overall: IN_PROGRESS, referenceCoverage = PARTIAL.
 *     (Batch 01 = DB_天干 + DB_地支; Batch 02 = DB_藏干; Batch 03 = DB_十神;
 *     Batch 04 = DB_60甲子. Remaining DB tables — Sheet43, Sheet47, and any
 *     others required by later batches — NOT_STARTED.)
 *
 * SCOPE (STRICT)
 *   Only Sheet39!A2:G61 is migrated. The workbook UsedRange for Sheet39
 *   is A1:I83 but content outside A1:G61 (rows 62..83, columns H:I) is
 *   documentation / support-block content and is EXPLICITLY OUT OF SCOPE
 *   for this batch. It is neither transcribed nor referenced.
 *
 * ISOLATION
 *   Does NOT write into `src/engine/referenceData.js`. Production
 *   ReferenceData registry continues to hold only MOCK_POC placeholders.
 *   Does NOT wire into the main V2.9.9 pipeline.
 *   Does NOT modify M02 Batch 01/02/03 behaviour. Batch 01 references
 *   are imported ONLY for cross-validation in the runner — not as a
 *   substitute for the audited A:F Excel evidence stored here.
 *
 * UNICODE POLICY (MIGRATION_RULES.md → Encoding Warning)
 *   Every Chinese literal is constructed from its audited AscW/Hex code
 *   point via `String.fromCodePoint(...)`. No direct Chinese glyph
 *   appears inside code — a comment on each character constant or row
 *   states the canonical glyph for reader convenience.
 *
 * DATA POLICY
 *   All 60 rows are the authoritative Excel evidence supplied by the
 *   user (Golden Master read-only extraction). A/B/C/D/E/F/G are stored
 *   from the audited row values; A/D/F are NOT algorithmically
 *   reconstructed from Batch 01/02 references (per explicit user
 *   authorization for this batch). Batch 01 cross-checks are enforced
 *   in the validation runner only.
 *
 * LOOKUP CONTRACT
 *   Exact-match Unicode-safe equality on 干支 (Column B). Missing keys
 *   throw `MISSING_REFERENCE_KEY` (the M02 Batch 01/02/03 convention
 *   for AUTHORITATIVE_DATASET_COMPLETE datasets). No fuzzy match, no
 *   transliteration, no silent fallback.
 *
 * DETERMINISM
 *   No wall clock, no randomness, no LLM. Byte-wise identical output
 *   on every run. Freeze at dataset + rows + per-row level.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── 天干 (10 stems) — audited code points from Batch 01 (kept LOCAL) ───────
const CH_JIA  = String.fromCodePoint(0x7532); // 甲
const CH_YI   = String.fromCodePoint(0x4E59); // 乙
const CH_BING = String.fromCodePoint(0x4E19); // 丙
const CH_DING = String.fromCodePoint(0x4E01); // 丁
const CH_WU   = String.fromCodePoint(0x620A); // 戊
const CH_JI   = String.fromCodePoint(0x5DF1); // 己
const CH_GENG = String.fromCodePoint(0x5E9A); // 庚
const CH_XIN  = String.fromCodePoint(0x8F9B); // 辛
const CH_REN  = String.fromCodePoint(0x58EC); // 壬
const CH_GUI  = String.fromCodePoint(0x7678); // 癸

// ── 地支 (12 branches) — audited code points from Batch 01 (kept LOCAL) ────
const CH_ZI   = String.fromCodePoint(0x5B50); // 子
const CH_CHOU = String.fromCodePoint(0x4E11); // 丑
const CH_YIN  = String.fromCodePoint(0x5BC5); // 寅
const CH_MAO  = String.fromCodePoint(0x536F); // 卯
const CH_CHEN = String.fromCodePoint(0x8FB0); // 辰
const CH_SI   = String.fromCodePoint(0x5DF3); // 巳
const CH_WU_BR= String.fromCodePoint(0x5348); // 午
const CH_WEI  = String.fromCodePoint(0x672A); // 未
const CH_SHEN = String.fromCodePoint(0x7533); // 申
const CH_YOU  = String.fromCodePoint(0x9149); // 酉
const CH_XU   = String.fromCodePoint(0x620C); // 戌
const CH_HAI  = String.fromCodePoint(0x4EA5); // 亥

// ── 纳音 (30 unique values, each spans 2 consecutive records) ──────────────
// Each constant is built EXCLUSIVELY from the AscW code-point array
// supplied by the user in the Batch 04 audit fixture.
const NY_HAI_ZHONG_JIN   = String.fromCodePoint(28023, 20013, 37329); // 海中金
const NY_LU_ZHONG_HUO    = String.fromCodePoint(28809, 20013, 28779); // 炉中火
const NY_DA_LIN_MU       = String.fromCodePoint(22823, 26519, 26408); // 大林木
const NY_LU_PANG_TU      = String.fromCodePoint(36335, 26049, 22303); // 路旁土
const NY_JIAN_FENG_JIN   = String.fromCodePoint(21073, 38155, 37329); // 剑锋金
const NY_SHAN_TOU_HUO    = String.fromCodePoint(23665, 22836, 28779); // 山头火
const NY_JIAN_XIA_SHUI   = String.fromCodePoint(28071, 19979, 27700); // 涧下水
const NY_CHENG_TOU_TU    = String.fromCodePoint(22478, 22836, 22303); // 城头土
const NY_BAI_LA_JIN      = String.fromCodePoint(30333, 34593, 37329); // 白蜡金
const NY_YANG_LIU_MU     = String.fromCodePoint(26472, 26611, 26408); // 杨柳木
const NY_QUAN_ZHONG_SHUI = String.fromCodePoint(27849, 20013, 27700); // 泉中水
const NY_WU_SHANG_TU     = String.fromCodePoint(23627, 19978, 22303); // 屋上土
const NY_PI_LI_HUO       = String.fromCodePoint(38713, 38643, 28779); // 霹雳火
const NY_SONG_BAI_MU     = String.fromCodePoint(26494, 26575, 26408); // 松柏木
const NY_CHANG_LIU_SHUI  = String.fromCodePoint(38271, 27969, 27700); // 长流水
const NY_SHA_ZHONG_JIN   = String.fromCodePoint(27801, 20013, 37329); // 沙中金
const NY_SHAN_XIA_HUO    = String.fromCodePoint(23665, 19979, 28779); // 山下火
const NY_PING_DI_MU      = String.fromCodePoint(24179, 22320, 26408); // 平地木
const NY_BI_SHANG_TU     = String.fromCodePoint(22721, 19978, 22303); // 壁上土
const NY_JIN_BO_JIN      = String.fromCodePoint(37329, 31636, 37329); // 金箔金
const NY_FU_DENG_HUO     = String.fromCodePoint(35206, 28783, 28779); // 覆灯火
const NY_TIAN_HE_SHUI    = String.fromCodePoint(22825, 27827, 27700); // 天河水
const NY_DA_YI_TU        = String.fromCodePoint(22823, 39551, 22303); // 大驿土
const NY_CHAI_CHUAN_JIN  = String.fromCodePoint(38039, 38031, 37329); // 钗钏金
const NY_SANG_ZHE_MU     = String.fromCodePoint(26705, 26584, 26408); // 桑柘木
const NY_DA_XI_SHUI      = String.fromCodePoint(22823, 28330, 27700); // 大溪水
const NY_SHA_ZHONG_TU    = String.fromCodePoint(27801, 20013, 22303); // 沙中土
const NY_TIAN_SHANG_HUO  = String.fromCodePoint(22825, 19978, 28779); // 天上火
const NY_SHI_LIU_MU      = String.fromCodePoint(30707, 27060, 26408); // 石榴木
const NY_DA_HAI_SHUI     = String.fromCodePoint(22823, 28023, 27700); // 大海水

// ── Headers (audited Unicode) ──────────────────────────────────────────────
const HEADER_XU_HAO      = String.fromCodePoint(24207, 21495);                 // 序号
const HEADER_GAN_ZHI     = String.fromCodePoint(24178, 25903);                 // 干支
const HEADER_TIAN_GAN    = String.fromCodePoint(22825, 24178);                 // 天干
const HEADER_TIAN_GAN_XH = String.fromCodePoint(22825, 24178, 24207, 21495);   // 天干序号
const HEADER_DI_ZHI      = String.fromCodePoint(22320, 25903);                 // 地支
const HEADER_DI_ZHI_XH   = String.fromCodePoint(22320, 25903, 24207, 21495);   // 地支序号
const HEADER_NA_YIN      = String.fromCodePoint(32435, 38899);                 // 纳音

// ── 60 authoritative records (Excel Sheet39!A2:G61) ────────────────────────
// Each row explicitly stores the Excel Value2 fields A/D/F (integers) and
// AscW-authoritative Unicode strings for B/C/E/G. B is stored (not derived)
// per user policy; the `B === C + E` invariant is enforced by the runner.
const ROWS = Object.freeze([
  Object.freeze({ xuHao:  1, ganZhi: CH_JIA + CH_ZI,   tianGan: CH_JIA,  tianGanIndex:  1, diZhi: CH_ZI,   diZhiIndex:  1, naYin: NY_HAI_ZHONG_JIN   }), // 甲子 → 海中金
  Object.freeze({ xuHao:  2, ganZhi: CH_YI  + CH_CHOU, tianGan: CH_YI,   tianGanIndex:  2, diZhi: CH_CHOU, diZhiIndex:  2, naYin: NY_HAI_ZHONG_JIN   }), // 乙丑 → 海中金
  Object.freeze({ xuHao:  3, ganZhi: CH_BING+ CH_YIN,  tianGan: CH_BING, tianGanIndex:  3, diZhi: CH_YIN,  diZhiIndex:  3, naYin: NY_LU_ZHONG_HUO    }), // 丙寅 → 炉中火
  Object.freeze({ xuHao:  4, ganZhi: CH_DING+ CH_MAO,  tianGan: CH_DING, tianGanIndex:  4, diZhi: CH_MAO,  diZhiIndex:  4, naYin: NY_LU_ZHONG_HUO    }), // 丁卯 → 炉中火
  Object.freeze({ xuHao:  5, ganZhi: CH_WU  + CH_CHEN, tianGan: CH_WU,   tianGanIndex:  5, diZhi: CH_CHEN, diZhiIndex:  5, naYin: NY_DA_LIN_MU       }), // 戊辰 → 大林木
  Object.freeze({ xuHao:  6, ganZhi: CH_JI  + CH_SI,   tianGan: CH_JI,   tianGanIndex:  6, diZhi: CH_SI,   diZhiIndex:  6, naYin: NY_DA_LIN_MU       }), // 己巳 → 大林木
  Object.freeze({ xuHao:  7, ganZhi: CH_GENG+ CH_WU_BR,tianGan: CH_GENG, tianGanIndex:  7, diZhi: CH_WU_BR,diZhiIndex:  7, naYin: NY_LU_PANG_TU      }), // 庚午 → 路旁土
  Object.freeze({ xuHao:  8, ganZhi: CH_XIN + CH_WEI,  tianGan: CH_XIN,  tianGanIndex:  8, diZhi: CH_WEI,  diZhiIndex:  8, naYin: NY_LU_PANG_TU      }), // 辛未 → 路旁土
  Object.freeze({ xuHao:  9, ganZhi: CH_REN + CH_SHEN, tianGan: CH_REN,  tianGanIndex:  9, diZhi: CH_SHEN, diZhiIndex:  9, naYin: NY_JIAN_FENG_JIN   }), // 壬申 → 剑锋金
  Object.freeze({ xuHao: 10, ganZhi: CH_GUI + CH_YOU,  tianGan: CH_GUI,  tianGanIndex: 10, diZhi: CH_YOU,  diZhiIndex: 10, naYin: NY_JIAN_FENG_JIN   }), // 癸酉 → 剑锋金
  Object.freeze({ xuHao: 11, ganZhi: CH_JIA + CH_XU,   tianGan: CH_JIA,  tianGanIndex:  1, diZhi: CH_XU,   diZhiIndex: 11, naYin: NY_SHAN_TOU_HUO    }), // 甲戌 → 山头火
  Object.freeze({ xuHao: 12, ganZhi: CH_YI  + CH_HAI,  tianGan: CH_YI,   tianGanIndex:  2, diZhi: CH_HAI,  diZhiIndex: 12, naYin: NY_SHAN_TOU_HUO    }), // 乙亥 → 山头火
  Object.freeze({ xuHao: 13, ganZhi: CH_BING+ CH_ZI,   tianGan: CH_BING, tianGanIndex:  3, diZhi: CH_ZI,   diZhiIndex:  1, naYin: NY_JIAN_XIA_SHUI   }), // 丙子 → 涧下水
  Object.freeze({ xuHao: 14, ganZhi: CH_DING+ CH_CHOU, tianGan: CH_DING, tianGanIndex:  4, diZhi: CH_CHOU, diZhiIndex:  2, naYin: NY_JIAN_XIA_SHUI   }), // 丁丑 → 涧下水
  Object.freeze({ xuHao: 15, ganZhi: CH_WU  + CH_YIN,  tianGan: CH_WU,   tianGanIndex:  5, diZhi: CH_YIN,  diZhiIndex:  3, naYin: NY_CHENG_TOU_TU    }), // 戊寅 → 城头土
  Object.freeze({ xuHao: 16, ganZhi: CH_JI  + CH_MAO,  tianGan: CH_JI,   tianGanIndex:  6, diZhi: CH_MAO,  diZhiIndex:  4, naYin: NY_CHENG_TOU_TU    }), // 己卯 → 城头土
  Object.freeze({ xuHao: 17, ganZhi: CH_GENG+ CH_CHEN, tianGan: CH_GENG, tianGanIndex:  7, diZhi: CH_CHEN, diZhiIndex:  5, naYin: NY_BAI_LA_JIN      }), // 庚辰 → 白蜡金
  Object.freeze({ xuHao: 18, ganZhi: CH_XIN + CH_SI,   tianGan: CH_XIN,  tianGanIndex:  8, diZhi: CH_SI,   diZhiIndex:  6, naYin: NY_BAI_LA_JIN      }), // 辛巳 → 白蜡金
  Object.freeze({ xuHao: 19, ganZhi: CH_REN + CH_WU_BR,tianGan: CH_REN,  tianGanIndex:  9, diZhi: CH_WU_BR,diZhiIndex:  7, naYin: NY_YANG_LIU_MU     }), // 壬午 → 杨柳木
  Object.freeze({ xuHao: 20, ganZhi: CH_GUI + CH_WEI,  tianGan: CH_GUI,  tianGanIndex: 10, diZhi: CH_WEI,  diZhiIndex:  8, naYin: NY_YANG_LIU_MU     }), // 癸未 → 杨柳木
  Object.freeze({ xuHao: 21, ganZhi: CH_JIA + CH_SHEN, tianGan: CH_JIA,  tianGanIndex:  1, diZhi: CH_SHEN, diZhiIndex:  9, naYin: NY_QUAN_ZHONG_SHUI }), // 甲申 → 泉中水
  Object.freeze({ xuHao: 22, ganZhi: CH_YI  + CH_YOU,  tianGan: CH_YI,   tianGanIndex:  2, diZhi: CH_YOU,  diZhiIndex: 10, naYin: NY_QUAN_ZHONG_SHUI }), // 乙酉 → 泉中水
  Object.freeze({ xuHao: 23, ganZhi: CH_BING+ CH_XU,   tianGan: CH_BING, tianGanIndex:  3, diZhi: CH_XU,   diZhiIndex: 11, naYin: NY_WU_SHANG_TU     }), // 丙戌 → 屋上土
  Object.freeze({ xuHao: 24, ganZhi: CH_DING+ CH_HAI,  tianGan: CH_DING, tianGanIndex:  4, diZhi: CH_HAI,  diZhiIndex: 12, naYin: NY_WU_SHANG_TU     }), // 丁亥 → 屋上土
  Object.freeze({ xuHao: 25, ganZhi: CH_WU  + CH_ZI,   tianGan: CH_WU,   tianGanIndex:  5, diZhi: CH_ZI,   diZhiIndex:  1, naYin: NY_PI_LI_HUO       }), // 戊子 → 霹雳火
  Object.freeze({ xuHao: 26, ganZhi: CH_JI  + CH_CHOU, tianGan: CH_JI,   tianGanIndex:  6, diZhi: CH_CHOU, diZhiIndex:  2, naYin: NY_PI_LI_HUO       }), // 己丑 → 霹雳火
  Object.freeze({ xuHao: 27, ganZhi: CH_GENG+ CH_YIN,  tianGan: CH_GENG, tianGanIndex:  7, diZhi: CH_YIN,  diZhiIndex:  3, naYin: NY_SONG_BAI_MU     }), // 庚寅 → 松柏木
  Object.freeze({ xuHao: 28, ganZhi: CH_XIN + CH_MAO,  tianGan: CH_XIN,  tianGanIndex:  8, diZhi: CH_MAO,  diZhiIndex:  4, naYin: NY_SONG_BAI_MU     }), // 辛卯 → 松柏木
  Object.freeze({ xuHao: 29, ganZhi: CH_REN + CH_CHEN, tianGan: CH_REN,  tianGanIndex:  9, diZhi: CH_CHEN, diZhiIndex:  5, naYin: NY_CHANG_LIU_SHUI  }), // 壬辰 → 长流水
  Object.freeze({ xuHao: 30, ganZhi: CH_GUI + CH_SI,   tianGan: CH_GUI,  tianGanIndex: 10, diZhi: CH_SI,   diZhiIndex:  6, naYin: NY_CHANG_LIU_SHUI  }), // 癸巳 → 长流水
  Object.freeze({ xuHao: 31, ganZhi: CH_JIA + CH_WU_BR,tianGan: CH_JIA,  tianGanIndex:  1, diZhi: CH_WU_BR,diZhiIndex:  7, naYin: NY_SHA_ZHONG_JIN   }), // 甲午 → 沙中金
  Object.freeze({ xuHao: 32, ganZhi: CH_YI  + CH_WEI,  tianGan: CH_YI,   tianGanIndex:  2, diZhi: CH_WEI,  diZhiIndex:  8, naYin: NY_SHA_ZHONG_JIN   }), // 乙未 → 沙中金
  Object.freeze({ xuHao: 33, ganZhi: CH_BING+ CH_SHEN, tianGan: CH_BING, tianGanIndex:  3, diZhi: CH_SHEN, diZhiIndex:  9, naYin: NY_SHAN_XIA_HUO    }), // 丙申 → 山下火
  Object.freeze({ xuHao: 34, ganZhi: CH_DING+ CH_YOU,  tianGan: CH_DING, tianGanIndex:  4, diZhi: CH_YOU,  diZhiIndex: 10, naYin: NY_SHAN_XIA_HUO    }), // 丁酉 → 山下火
  Object.freeze({ xuHao: 35, ganZhi: CH_WU  + CH_XU,   tianGan: CH_WU,   tianGanIndex:  5, diZhi: CH_XU,   diZhiIndex: 11, naYin: NY_PING_DI_MU      }), // 戊戌 → 平地木
  Object.freeze({ xuHao: 36, ganZhi: CH_JI  + CH_HAI,  tianGan: CH_JI,   tianGanIndex:  6, diZhi: CH_HAI,  diZhiIndex: 12, naYin: NY_PING_DI_MU      }), // 己亥 → 平地木
  Object.freeze({ xuHao: 37, ganZhi: CH_GENG+ CH_ZI,   tianGan: CH_GENG, tianGanIndex:  7, diZhi: CH_ZI,   diZhiIndex:  1, naYin: NY_BI_SHANG_TU     }), // 庚子 → 壁上土
  Object.freeze({ xuHao: 38, ganZhi: CH_XIN + CH_CHOU, tianGan: CH_XIN,  tianGanIndex:  8, diZhi: CH_CHOU, diZhiIndex:  2, naYin: NY_BI_SHANG_TU     }), // 辛丑 → 壁上土
  Object.freeze({ xuHao: 39, ganZhi: CH_REN + CH_YIN,  tianGan: CH_REN,  tianGanIndex:  9, diZhi: CH_YIN,  diZhiIndex:  3, naYin: NY_JIN_BO_JIN      }), // 壬寅 → 金箔金
  Object.freeze({ xuHao: 40, ganZhi: CH_GUI + CH_MAO,  tianGan: CH_GUI,  tianGanIndex: 10, diZhi: CH_MAO,  diZhiIndex:  4, naYin: NY_JIN_BO_JIN      }), // 癸卯 → 金箔金
  Object.freeze({ xuHao: 41, ganZhi: CH_JIA + CH_CHEN, tianGan: CH_JIA,  tianGanIndex:  1, diZhi: CH_CHEN, diZhiIndex:  5, naYin: NY_FU_DENG_HUO     }), // 甲辰 → 覆灯火
  Object.freeze({ xuHao: 42, ganZhi: CH_YI  + CH_SI,   tianGan: CH_YI,   tianGanIndex:  2, diZhi: CH_SI,   diZhiIndex:  6, naYin: NY_FU_DENG_HUO     }), // 乙巳 → 覆灯火
  Object.freeze({ xuHao: 43, ganZhi: CH_BING+ CH_WU_BR,tianGan: CH_BING, tianGanIndex:  3, diZhi: CH_WU_BR,diZhiIndex:  7, naYin: NY_TIAN_HE_SHUI    }), // 丙午 → 天河水
  Object.freeze({ xuHao: 44, ganZhi: CH_DING+ CH_WEI,  tianGan: CH_DING, tianGanIndex:  4, diZhi: CH_WEI,  diZhiIndex:  8, naYin: NY_TIAN_HE_SHUI    }), // 丁未 → 天河水
  Object.freeze({ xuHao: 45, ganZhi: CH_WU  + CH_SHEN, tianGan: CH_WU,   tianGanIndex:  5, diZhi: CH_SHEN, diZhiIndex:  9, naYin: NY_DA_YI_TU        }), // 戊申 → 大驿土
  Object.freeze({ xuHao: 46, ganZhi: CH_JI  + CH_YOU,  tianGan: CH_JI,   tianGanIndex:  6, diZhi: CH_YOU,  diZhiIndex: 10, naYin: NY_DA_YI_TU        }), // 己酉 → 大驿土
  Object.freeze({ xuHao: 47, ganZhi: CH_GENG+ CH_XU,   tianGan: CH_GENG, tianGanIndex:  7, diZhi: CH_XU,   diZhiIndex: 11, naYin: NY_CHAI_CHUAN_JIN  }), // 庚戌 → 钗钏金
  Object.freeze({ xuHao: 48, ganZhi: CH_XIN + CH_HAI,  tianGan: CH_XIN,  tianGanIndex:  8, diZhi: CH_HAI,  diZhiIndex: 12, naYin: NY_CHAI_CHUAN_JIN  }), // 辛亥 → 钗钏金
  Object.freeze({ xuHao: 49, ganZhi: CH_REN + CH_ZI,   tianGan: CH_REN,  tianGanIndex:  9, diZhi: CH_ZI,   diZhiIndex:  1, naYin: NY_SANG_ZHE_MU     }), // 壬子 → 桑柘木
  Object.freeze({ xuHao: 50, ganZhi: CH_GUI + CH_CHOU, tianGan: CH_GUI,  tianGanIndex: 10, diZhi: CH_CHOU, diZhiIndex:  2, naYin: NY_SANG_ZHE_MU     }), // 癸丑 → 桑柘木
  Object.freeze({ xuHao: 51, ganZhi: CH_JIA + CH_YIN,  tianGan: CH_JIA,  tianGanIndex:  1, diZhi: CH_YIN,  diZhiIndex:  3, naYin: NY_DA_XI_SHUI      }), // 甲寅 → 大溪水
  Object.freeze({ xuHao: 52, ganZhi: CH_YI  + CH_MAO,  tianGan: CH_YI,   tianGanIndex:  2, diZhi: CH_MAO,  diZhiIndex:  4, naYin: NY_DA_XI_SHUI      }), // 乙卯 → 大溪水
  Object.freeze({ xuHao: 53, ganZhi: CH_BING+ CH_CHEN, tianGan: CH_BING, tianGanIndex:  3, diZhi: CH_CHEN, diZhiIndex:  5, naYin: NY_SHA_ZHONG_TU    }), // 丙辰 → 沙中土
  Object.freeze({ xuHao: 54, ganZhi: CH_DING+ CH_SI,   tianGan: CH_DING, tianGanIndex:  4, diZhi: CH_SI,   diZhiIndex:  6, naYin: NY_SHA_ZHONG_TU    }), // 丁巳 → 沙中土
  Object.freeze({ xuHao: 55, ganZhi: CH_WU  + CH_WU_BR,tianGan: CH_WU,   tianGanIndex:  5, diZhi: CH_WU_BR,diZhiIndex:  7, naYin: NY_TIAN_SHANG_HUO  }), // 戊午 → 天上火
  Object.freeze({ xuHao: 56, ganZhi: CH_JI  + CH_WEI,  tianGan: CH_JI,   tianGanIndex:  6, diZhi: CH_WEI,  diZhiIndex:  8, naYin: NY_TIAN_SHANG_HUO  }), // 己未 → 天上火
  Object.freeze({ xuHao: 57, ganZhi: CH_GENG+ CH_SHEN, tianGan: CH_GENG, tianGanIndex:  7, diZhi: CH_SHEN, diZhiIndex:  9, naYin: NY_SHI_LIU_MU      }), // 庚申 → 石榴木
  Object.freeze({ xuHao: 58, ganZhi: CH_XIN + CH_YOU,  tianGan: CH_XIN,  tianGanIndex:  8, diZhi: CH_YOU,  diZhiIndex: 10, naYin: NY_SHI_LIU_MU      }), // 辛酉 → 石榴木
  Object.freeze({ xuHao: 59, ganZhi: CH_REN + CH_XU,   tianGan: CH_REN,  tianGanIndex:  9, diZhi: CH_XU,   diZhiIndex: 11, naYin: NY_DA_HAI_SHUI     }), // 壬戌 → 大海水
  Object.freeze({ xuHao: 60, ganZhi: CH_GUI + CH_HAI,  tianGan: CH_GUI,  tianGanIndex: 10, diZhi: CH_HAI,  diZhiIndex: 12, naYin: NY_DA_HAI_SHUI     }), // 癸亥 → 大海水
]);

/**
 * DB_60甲子 — 60-row 甲子 cycle with 纳音 (Sheet39 A1:G61).
 * Frozen at every level. Rows are the AUTHORITATIVE Excel evidence.
 */
export const DB_LIU_SHI_JIA_ZI = Object.freeze({
  codeName: "Sheet39",
  worksheetIndex: 39,
  sheetName: String.fromCodePoint(0x44, 0x42, 0x5F, 0x36, 0x30, 30002, 23376), // DB_60甲子
  // NB: sheetName uses U+7532 甲 (30002) + U+5B50 子 (23376). Also stored below.
  sheetNameUnicode: [68, 66, 95, 54, 48, 30002, 23376],
  excelUsedRange: "A1:G61", // authoritative migrated range (subset of full A1:I83)
  workbookFullUsedRange: "A1:I83",
  outOfScopeAreas: Object.freeze({
    // Documented as OUT_OF_SCOPE for this batch — evidence only.
    row62_documentationSupport: true,
    row69_blank: true,
    row70_startOfSupportSection: true,
    rows71to81_validationBlock_9cols: true,
    row82_blank: true,
    row83_supportCells: true,
    columnsHtoI_supportBlock: true,
  }),
  status: "AUTHORITATIVE_DATASET_COMPLETE",
  evidenceProvenance: "read-only Golden Master Value2 + AscW extraction",
  auditedInBatch: "M02_BATCH_04",
  headers: Object.freeze({
    A: HEADER_XU_HAO,      // 序号
    B: HEADER_GAN_ZHI,     // 干支
    C: HEADER_TIAN_GAN,    // 天干
    D: HEADER_TIAN_GAN_XH, // 天干序号
    E: HEADER_DI_ZHI,      // 地支
    F: HEADER_DI_ZHI_XH,   // 地支序号
    G: HEADER_NA_YIN,      // 纳音
  }),
  rows: ROWS,
});

// ── Lookup API ─────────────────────────────────────────────────────────────

function throwMissingKey(column, key) {
  const err = new Error(
    `[M02·DB_60甲子] ${column} key ${JSON.stringify(key)} not found in ` +
      "AUTHORITATIVE_DATASET_COMPLETE (60 audited rows). No fuzzy match or " +
      "substitution is performed."
  );
  err.code = "MISSING_REFERENCE_KEY";
  err.dataset = "DB_60甲子";
  err.column = column;
  err.key = key;
  throw err;
}

/**
 * Exact-match lookup by 干支 (Column B). Returns the audited 纳音
 * (Column G) or throws `MISSING_REFERENCE_KEY`.
 *
 * Sheet27 B89 formula family:
 *   =IFERROR(VLOOKUP(B38, DB_60甲子!B:G, 6, FALSE()), "")
 * uses this exact lookup: lookupKey=干支, returnColumn=纳音.
 *
 * @param {string} ganZhi audited 干支 two-glyph string (e.g. "丁巳")
 * @returns {string} audited 纳音 three-glyph string (e.g. "沙中土")
 */
export function liuShiJiaZiLookup(ganZhi) {
  if (typeof ganZhi !== "string") {
    throw new TypeError("[M02·DB_60甲子] ganZhi must be a string");
  }
  const row = ROWS.find((r) => r.ganZhi === ganZhi);
  if (!row) throwMissingKey("干支", ganZhi);
  return row.naYin;
}

/**
 * Exact-match lookup by 干支 (Column B). Returns the FULL frozen row
 * (xuHao, ganZhi, tianGan, tianGanIndex, diZhi, diZhiIndex, naYin) or
 * throws `MISSING_REFERENCE_KEY`.
 */
export function liuShiJiaZiRowLookup(ganZhi) {
  if (typeof ganZhi !== "string") {
    throw new TypeError("[M02·DB_60甲子] ganZhi must be a string");
  }
  const row = ROWS.find((r) => r.ganZhi === ganZhi);
  if (!row) throwMissingKey("干支Row", ganZhi);
  return row;
}

/**
 * Exact-match lookup by 序号 (Column A, 1..60). Returns the FULL frozen
 * row or throws `MISSING_REFERENCE_KEY`.
 */
export function liuShiJiaZiRowByXuHao(xuHao) {
  if (typeof xuHao !== "number" || !Number.isInteger(xuHao)) {
    throw new TypeError("[M02·DB_60甲子] xuHao must be an integer");
  }
  const row = ROWS.find((r) => r.xuHao === xuHao);
  if (!row) throwMissingKey("序号", xuHao);
  return row;
}
